import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Calculator, Users, Calendar, Euro } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { calculateBudgetBreakdown, EventType, PriceTier, Services, eventTypeLabels, priceTierLabels } from "@/utils/priceCalculations";
import EmailCaptureModal from "@/components/calculators/EmailCaptureModal";
import { sendSlackMessage } from "@/lib/sendSlackMessage";
import { getInitialUrl, getStoredUTMParams } from "@/lib/analytics";
import { getCampaignName } from "@/lib/campaignMapping";
import { toast } from "sonner";
const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6"];
const BudgetEstimatorSection = () => {
  const [eventType, setEventType] = useState<EventType>("corporate_retreat");
  const [priceTier, setPriceTier] = useState<PriceTier>("standard");
  const [participants, setParticipants] = useState(20);
  const [days, setDays] = useState(2);
  const [services, setServices] = useState<Services>({
    restauration: true,
    transport: false,
    activities: true
  });
  const [isResultsRevealed, setIsResultsRevealed] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const breakdown = useMemo(() => {
    return calculateBudgetBreakdown(eventType, priceTier, participants, days, services);
  }, [eventType, priceTier, participants, days, services]);
  const chartData = useMemo(() => {
    const data = [];
    if (breakdown.venue > 0) data.push({
      name: "Lieu",
      value: breakdown.venue
    });
    if (breakdown.restauration > 0) data.push({
      name: "Restauration",
      value: breakdown.restauration
    });
    if (breakdown.transport > 0) data.push({
      name: "Transport",
      value: breakdown.transport
    });
    if (breakdown.activities > 0) data.push({
      name: "Activités",
      value: breakdown.activities
    });
    return data;
  }, [breakdown]);
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0
    }).format(value);
  };
  const handleEmailSubmit = async (email: string) => {
    setIsLoading(true);
    try {
      const slackChannel = "C0B0LPU0SCS";
      const servicesText = [services.restauration && "Restauration", services.transport && "Transport", services.activities && "Activités"].filter(Boolean).join(", ") || "Aucun";
      const initialUrl = getInitialUrl();

      // Get UTM parameters and campaign name
      const storedUTMs = getStoredUTMParams();
      const urlParams = new URLSearchParams(new URL(initialUrl).search);
      const campaignName = getCampaignName(urlParams, storedUTMs);
      const utmSource = storedUTMs?.utm_source || urlParams.get('utm_source') || '';
      const utmMedium = storedUTMs?.utm_medium || urlParams.get('utm_medium') || '';
      const utmContent = storedUTMs?.utm_content || urlParams.get('utm_content') || '';
      const sdr = storedUTMs?.utm_sdr || urlParams.get('utm_sdr') || '—';
      const message = `🧮 *Nouveau calcul de budget*
*Email* : ${email}
*Market* : FR 🇫🇷
*SDR* : ${sdr}
*Type d'événement* : ${eventTypeLabels[eventType]}
*Gamme* : ${priceTierLabels[priceTier]}
*Participants* : ${participants}
*Jours* : ${days}
*Services* : ${servicesText}
*Budget estimé* : ${formatCurrency(breakdown.total)}
*Par personne* : ${formatCurrency(Math.round(breakdown.total / participants))}
${campaignName ? `*Campagne* : ${campaignName}` : ''}
${utmSource ? `*Source* : ${utmSource}` : ''}
${utmMedium ? `*Medium* : ${utmMedium}` : ''}
${utmContent ? `*Contenu* : ${utmContent}` : ''}`;
      await sendSlackMessage(message, slackChannel);
      setShowEmailModal(false);
      setIsResultsRevealed(true);
      toast.success("Votre budget a été calculé avec succès !");
    } catch (error) {
      console.error("Erreur lors de l'envoi Slack:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };
  return <section className="py-16 md:py-24 px-4 bg-background">
      <div className="container mx-auto max-w-6xl">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12">
          <div className="inline-flex items-center bg-[#C6E278] text-black px-4 py-2 rounded-full text-sm font-normal mb-4">
            <Calculator className="w-4 h-4 mr-2" />
            Outil gratuit
          </div>
          <h2 className="text-3xl md:text-4xl text-foreground mb-4 font-normal">
            Calculateur de Budget
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Estimez le budget de votre événement d'entreprise en quelques clics
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Form */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="space-y-8">
            {/* Event Type */}
            <div className="space-y-3">
              <Label className="text-base font-normal">Type d'événement</Label>
              <Select value={eventType} onValueChange={(value) => setEventType(value as EventType)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(eventTypeLabels).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Price Tier */}
            <div className="space-y-3">
              <Label className="text-base font-normal">Gamme de prix</Label>
              <div className="grid grid-cols-3 gap-3">
                {(Object.keys(priceTierLabels) as PriceTier[]).map((tier) => <Button key={tier} variant={priceTier === tier ? "default" : "outline"} onClick={() => setPriceTier(tier)} className={priceTier === tier ? "bg-primary text-primary-foreground" : ""}>
                    {priceTierLabels[tier]}
                  </Button>)}
              </div>
            </div>

            {/* Participants */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-normal flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Nombre de participants
                </Label>
                <span className="font-semibold text-primary">{participants}</span>
              </div>
              <Slider value={[participants]} onValueChange={(value) => setParticipants(value[0])} min={5} max={500} step={5} className="w-full" />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>5</span>
                <span>500</span>
              </div>
            </div>

            {/* Days */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-normal flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Nombre de jours
                </Label>
                <span className="font-semibold text-primary">{days}</span>
              </div>
              <Slider value={[days]} onValueChange={(value) => setDays(value[0])} min={1} max={5} step={1} className="w-full" />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>1 jour</span>
                <span>5 jours</span>
              </div>
            </div>

            {/* Services */}
            <div className="space-y-3">
              <Label className="text-base font-normal">Services inclus</Label>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Checkbox id="restauration" checked={services.restauration} onCheckedChange={(checked) => setServices((prev) => ({
                  ...prev,
                  restauration: !!checked
                }))} />
                  <label htmlFor="restauration" className="text-sm cursor-pointer">Restauration</label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox id="transport" checked={services.transport} onCheckedChange={(checked) => setServices((prev) => ({
                  ...prev,
                  transport: !!checked
                }))} />
                  <label htmlFor="transport" className="text-sm cursor-pointer">Transport</label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox id="activities" checked={services.activities} onCheckedChange={(checked) => setServices((prev) => ({
                  ...prev,
                  activities: !!checked
                }))} />
                  <label htmlFor="activities" className="text-sm cursor-pointer">Activités</label>
                </div>
              </div>
            </div>

          </motion.div>

          {/* Results */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-muted rounded-2xl p-8 relative">
            {/* Container for blurred content and CTA overlay */}
            <div className="relative">
              {/* CTA Button centered over blurred content */}
              {!isResultsRevealed && <div className="absolute inset-0 flex items-center justify-center z-10">
                  <Button onClick={() => setShowEmailModal(true)} className="bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 hover:scale-105 hover:shadow-xl hover:shadow-cta-green/30 rounded-md transition-all duration-300 py-6 text-sm" size="lg">
                    <Calculator className="w-5 h-5 mr-2" />
                    Découvrir mon budget
                  </Button>
                </div>}
              
              {/* Blurred content */}
              <div className={`transition-all duration-500 ${!isResultsRevealed ? 'blur-md pointer-events-none select-none' : ''}`}>
              <div className="text-center mb-8">
                <p className="text-sm text-muted-foreground mb-2">Budget estimé</p>
                <div className="flex items-center justify-center gap-2">
                  <Euro className="w-8 h-8 text-primary" />
                  <span className="text-4xl md:text-5xl font-bold text-foreground">
                    {formatCurrency(breakdown.total)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {formatCurrency(Math.round(breakdown.total / participants))} par personne
                </p>
              </div>

              {chartData.length > 0 && <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={chartData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                        {chartData.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>}

              <div className="mt-6 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Lieu</span>
                  <span className="font-normal">{formatCurrency(breakdown.venue)}</span>
                </div>
                {services.restauration && <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Restauration</span>
                    <span className="font-normal">{formatCurrency(breakdown.restauration)}</span>
                  </div>}
                {services.transport && <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Transport</span>
                    <span className="font-normal">{formatCurrency(breakdown.transport)}</span>
                  </div>}
                {services.activities && <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Activités</span>
                    <span className="font-normal">{formatCurrency(breakdown.activities)}</span>
                  </div>}
              </div>
              </div>
            </div>

            {/* Email Capture Modal Overlay */}
            <EmailCaptureModal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} onSubmit={handleEmailSubmit} type="budget" isLoading={isLoading} />
          </motion.div>
        </div>
      </div>
    </section>;
};
export default BudgetEstimatorSection;